/**
 * TemplateTable – Shared table component for all template category tabs.
 *
 * Uses a proper Mantine <Table> inside a <Paper> wrapper matching theme.ts.
 *
 * Layout per design screenshots:
 * - Checkbox (Unify Admin) for multi-select status toggles
 * - Switch column (active/enabled status; Unify Admin only)
 * - Template name (clickable link for Unify Admin → Template Editor; plain text for others)
 * - "(Default)" italic label for default templates
 * - Action icons: Preview (eye); Duplicate/Delete (Unify Admin only)
 */
import {
  ActionIcon,
  Box,
  Checkbox,
  Group,
  Paper,
  Switch,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconEye,
  IconCopy,
  IconTrash,
} from '@tabler/icons-react';
import type { TemplateListItem } from '../types';

interface TemplateTableProps {
  templates: TemplateListItem[];
  /** Eye icon: opens Preview (editable, non-persistent). */
  onPreview: (template: TemplateListItem) => void;
  onDuplicate: (template: TemplateListItem) => void;
  onDelete: (template: TemplateListItem) => void;
  onToggleActive: (template: TemplateListItem) => void;
  /** Click on template name: opens Template Editor (persistent editing). */
  onClickName: (template: TemplateListItem) => void;
  /** When false, name is not clickable, duplicate/delete are hidden, and status toggle is read-only. */
  canManageTemplates?: boolean;
  /** When true, hides the delete action (prefer archive over delete). */
  hideDelete?: boolean;
  /** Per-row lock: preview only (Diagnosis & Treatment Plan). */
  isTemplateLocked?: (template: TemplateListItem) => boolean;
  loading?: boolean;
  selectedIds?: readonly string[];
  onSelectedIdsChange?: (ids: string[]) => void;
  toggling?: boolean;
}

export function TemplateTable({
  templates,
  onPreview,
  onDuplicate,
  onDelete,
  onToggleActive,
  onClickName,
  canManageTemplates = true,
  hideDelete = false,
  isTemplateLocked,
  loading = false,
  selectedIds = [],
  onSelectedIdsChange,
  toggling = false,
}: TemplateTableProps): JSX.Element {
  if (loading) {
    return (
      <Box py="xl" ta="center">
        <Text size="sm" c="dimmed">
          Loading templates…
        </Text>
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Paper p="lg" withBorder ta="center">
        <Text size="sm" c="dimmed">
          No templates found. Create one to get started.
        </Text>
      </Paper>
    );
  }

  const selectedSet = new Set(selectedIds);
  const selectableTemplates = templates.filter((t) => !isTemplateLocked?.(t));
  const allSelected =
    selectableTemplates.length > 0 && selectableTemplates.every((t) => selectedSet.has(t.id));
  const someSelected = selectableTemplates.some((t) => selectedSet.has(t.id));
  const showSelection = canManageTemplates && onSelectedIdsChange != null;

  const toggleRow = (id: string) => {
    if (!onSelectedIdsChange) return;
    const template = templates.find((t) => t.id === id);
    if (template && isTemplateLocked?.(template)) return;
    if (selectedSet.has(id)) {
      onSelectedIdsChange(selectedIds.filter((value) => value !== id));
      return;
    }
    onSelectedIdsChange([...selectedIds, id]);
  };

  const toggleAll = () => {
    if (!onSelectedIdsChange) return;
    onSelectedIdsChange(allSelected ? [] : selectableTemplates.map((t) => t.id));
  };

  return (
    <Paper p="lg" withBorder>
      <Table highlightOnHover>
        {showSelection ? (
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 36, paddingRight: 0 }}>
                <Checkbox
                  color="gold"
                  aria-label="Select all templates"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleAll}
                />
              </Table.Th>
              <Table.Th style={{ width: 52 }} />
              <Table.Th>
                <Text size="xs" c="dimmed" fw={500}>
                  Template
                </Text>
              </Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
        ) : null}
        <Table.Tbody>
          {templates.map((template) => {
            const rowLocked = Boolean(isTemplateLocked?.(template));
            const rowCanManage = canManageTemplates && !rowLocked;
            return (
            <Table.Tr key={template.id} bg={selectedSet.has(template.id) ? 'gray.0' : undefined}>
              {showSelection ? (
                <Table.Td style={{ width: 36, paddingRight: 0 }}>
                  <Checkbox
                    color="gold"
                    aria-label={`Select ${template.title}`}
                    checked={selectedSet.has(template.id)}
                    disabled={rowLocked}
                    onChange={() => toggleRow(template.id)}
                  />
                </Table.Td>
              ) : null}
              <Table.Td style={{ width: 52, paddingRight: 0 }}>
                <Switch
                  color="gold"
                  checked={template.status === 'active'}
                  onChange={() => onToggleActive(template)}
                  disabled={!rowCanManage || toggling}
                  size="sm"
                  aria-label={
                    rowCanManage
                      ? `${template.status === 'active' ? 'Deactivate' : 'Activate'} ${template.title}`
                      : `${template.title} is ${template.status === 'active' ? 'active' : 'inactive'}`
                  }
                />
              </Table.Td>

              <Table.Td>
                {rowCanManage ? (
                  <Text
                    size="sm"
                    c="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onClickName(template)}
                  >
                    {template.title}
                    {template.isDefault && (
                      <Text component="span" size="sm" fs="italic" c="dimmed" ml={4}>
                        (Default)
                      </Text>
                    )}
                  </Text>
                ) : (
                  <Text size="sm">
                    {template.title}
                    {template.isDefault && (
                      <Text component="span" size="sm" fs="italic" c="dimmed" ml={4}>
                        (Default)
                      </Text>
                    )}
                  </Text>
                )}
              </Table.Td>

              <Table.Td
                style={{
                  width: rowCanManage ? (hideDelete ? 80 : 110) : 48,
                  textAlign: 'right',
                }}
              >
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <Tooltip label="Preview" withArrow position="top">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      size="sm"
                      onClick={() => onPreview(template)}
                      aria-label={`Preview ${template.title}`}
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  </Tooltip>
                  {rowCanManage && (
                    <Tooltip label="Duplicate" withArrow position="top">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        size="sm"
                        onClick={() => onDuplicate(template)}
                        aria-label={`Duplicate ${template.title}`}
                      >
                        <IconCopy size={18} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {rowCanManage && !hideDelete && (
                    <Tooltip label="Delete" withArrow position="top">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        size="sm"
                        onClick={() => onDelete(template)}
                        aria-label={`Delete ${template.title}`}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
